import { FC } from 'react'
import cls from '../../styles/StaticContent.module.scss'
import { HeaderBar } from '@/components/HeaderBar'
import { GetServerSideProps } from 'next'
import { SEO } from '@/components/SEO'
import { Footer } from '@/components/Footer'

interface SSP {
  insideTeams: boolean
}

const GettingStartedArticle: FC<SSP> = ({ insideTeams }) => {
  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Getting Started with Delayed Send for Microsoft Teams"
        description="Learn how to use Delayed Send for Microsoft Teams to schedule messages for future delivery, improving your communication workflow and team coordination."
        keywords="delayed send Teams, schedule messages Teams, Microsoft Teams scheduling, future message delivery"
        article={true}
      />
      <HeaderBar showLogOutButton={false} className={insideTeams ? '!hidden' : ''} />
      <div className={`${cls.container} flex-grow`}>
        <h1>Getting Started with Delayed Send for Microsoft Teams</h1>

        <p>
          Effective communication is essential for team productivity, but sending messages at the right time 
          can be challenging, especially with remote teams across different time zones. Delayed Send 
          for Microsoft Teams solves this problem by allowing you to schedule messages to be sent at the 
          perfect time, ensuring your communication is both timely and respectful of others' working hours.
        </p>

        <h2>Why Use Delayed Send for Microsoft Teams?</h2>

        <p>
          Delayed Send offers several key benefits that can transform how your team communicates:
        </p>

        <ul>
          <li><strong>Schedule messages for optimal timing</strong> - Send notifications when recipients are most likely to see them</li>
          <li><strong>Respect work-life balance</strong> - Compose messages during your working hours, but deliver them during recipients' working hours</li>
          <li><strong>Plan communication in advance</strong> - Prepare announcements, updates, and follow-ups ahead of time</li>
          <li><strong>Reduce communication stress</strong> - No more feeling pressured to respond to messages outside of working hours</li>
        </ul>

        <h2>Getting Started: Installation and Setup</h2>

        <h3>Installing Delayed Send</h3>

        <ol>
          <li>
            <strong>Open Microsoft Teams</strong> and click on the Apps icon in the left sidebar
          </li>
          <li>
            <strong>Search for "Delayed Send"</strong> in the search bar
          </li>
          <li>
            <strong>Select the app</strong> from the search results
          </li>
          <li>
            <strong>Click "Add"</strong> to install the app for your Teams account
          </li>
          <li>
            <strong>Grant permissions</strong> when prompted to allow the app to send messages and access necessary data
          </li>
        </ol>

        <p>
          Once installed, you'll see the Delayed Send app in your left sidebar and as an available 
          extension when composing messages.
        </p>

        <h2>Creating Your First Delayed Message</h2>

        <p>
          You can create delayed messages in two ways: from the compose box or from the Delayed Send dashboard.
        </p>

        <h3>Method 1: Using the Compose Box Extension</h3>

        <ol>
          <li>
            <strong>Navigate to any chat or channel</strong> where you want to send a delayed message
          </li>
          <li>
            <strong>Click on the "+" icon</strong> below the compose box to open extensions
          </li>
          <li>
            <strong>Select "Delayed Send"</strong> from the available options
          </li>
          <li>
            <strong>Compose your message</strong> using the rich text editor
          </li>
          <li>
            <strong>Set the delivery date and time</strong> using the date and time picker
          </li>
          <li>
            <strong>Click "Schedule"</strong> to queue your message for future delivery
          </li>
        </ol>

        <h3>Method 2: Using the Delayed Send Dashboard</h3>

        <ol>
          <li>
            <strong>Click on the Delayed Send app</strong> in your left sidebar
          </li>
          <li>
            <strong>Select "Create New Message"</strong> in the top right
          </li>
          <li>
            <strong>Select the target recipient</strong> (chat, channel, or person)
          </li>
          <li>
            <strong>Compose your message</strong> with the desired content
          </li>
          <li>
            <strong>Set the delivery time</strong> for when you want the message sent
          </li>
          <li>
            <strong>Click "Schedule Message"</strong> to add it to your queue
          </li>
        </ol>

        <h2>Managing Your Scheduled Messages</h2>

        <p>
          The Delayed Send dashboard provides a central location to manage all your scheduled messages:
        </p>

        <h3>Viewing Scheduled Messages</h3>

        <ul>
          <li>
            <strong>Open the Delayed Send app</strong> from your Teams sidebar
          </li>
          <li>
            <strong>View the "Pending" tab</strong> to see all messages scheduled for future delivery
          </li>
          <li>
            <strong>Check message status</strong> to confirm everything is set correctly
          </li>
        </ul>

        <h3>Editing or Canceling Messages</h3>

        <ul>
          <li>
            <strong>Locate the message</strong> you want to modify in the dashboard
          </li>
          <li>
            <strong>Click "Edit"</strong> to change the content or delivery time
          </li>
          <li>
            <strong>Click "Cancel"</strong> to remove the message from the queue entirely
          </li>
        </ul>

        <h2>Best Practices for Delayed Messages</h2>

        <h3>Timing Considerations</h3>

        <ul>
          <li>
            <strong>Consider recipient time zones</strong> - Schedule for their working hours
          </li>
          <li>
            <strong>Avoid scheduling on weekends or holidays</strong> - Unless specifically needed
          </li>
          <li>
            <strong>Use morning schedules for action items</strong> - People tend to be more productive earlier in the day
          </li>
          <li>
            <strong>Schedule FYI messages for afternoon/evening</strong> - When they won't interrupt deep work
          </li>
        </ul>

        <h3>Content Best Practices</h3>

        <ul>
          <li>
            <strong>Be clear about time-sensitive information</strong> - Include deadlines explicitly
          </li>
          <li>
            <strong>Provide context</strong> - Remember that circumstances might change between composition and delivery
          </li>
          <li>
            <strong>Use formatting</strong> to highlight important points
          </li>
          <li>
            <strong>Consider creating message templates</strong> for recurring communications
          </li>
        </ul>

        <h2>Troubleshooting Common Issues</h2>

        <h3>Messages Not Sending</h3>

        <ul>
          <li>Verify the app has proper permissions</li>
          <li>Check that your Teams status is active</li>
          <li>Confirm that the target chat or channel still exists</li>
          <li>Ensure your authentication hasn't expired</li>
        </ul>

        <h3>Other Common Issues</h3>

        <ul>
          <li>Refresh the app if the dashboard isn't showing updated information</li>
          <li>Check your internet connection if messages appear stuck</li>
          <li>Verify your Teams client is up to date</li>
        </ul>

        <h2>Conclusion</h2>

        <p>
          Delayed Send for Microsoft Teams transforms how you communicate, giving you control over 
          message timing and helping your team coordinate more effectively across time zones and schedules. 
          By scheduling your communications thoughtfully, you can ensure that important information reaches 
          your team when they're most receptive, while respecting their time and focus.
        </p>

        <p>
          As you become more familiar with the app, you'll discover even more ways to optimize your 
          communication workflows and improve productivity for your entire team.
        </p>

        <h3>Next Steps</h3>

        <p>
          Ready to take your Microsoft Teams communication to the next level? Explore our other guides 
          on <a href="/article/advanced-scheduling-techniques-delayed-send">advanced scheduling techniques</a> and 
          <a href="/article/effective-team-announcements-delayed-send">creating effective team announcements</a> 
          with Delayed Send.
        </p>
      </div>
      <Footer />
    </div>
  )
}

export default GettingStartedArticle

export const getServerSideProps: GetServerSideProps<SSP> = async (context) => {
  return {
    props: {
      insideTeams: !!context.query.teamsEmail,
    },
  }
}